import { Box, Icon, Step, StepIndicator, StepSeparator, StepStatus } from '@chakra-ui/react';
import { RefreshIcon } from '@heroicons/react/outline';
import {
  CashIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  PencilAltIcon,
  ShieldCheckIcon,
} from '@heroicons/react/solid';
import { ElementType } from 'react';

import { ActivityType } from '@blockpulse3/data/shared';
import {
  ActivityData,
  DocumentResetData,
  DocumentSignedData,
  InvestAmountReviewedData,
  SubscriptionValidatedData,
  TransferReceivedData,
} from '@blockpulse3/graphql/hooks';

import {
  DocumentSigned,
  SubscriptionCompleted,
  SubscriptionValidated,
  TransferReceived,
} from './Activities';
import { DocumentReset } from './Activities/DocumentReset';
import { InvestAmountReviewed } from './Activities/InvestAmountReviewed';

type Props = {
  date: Date;
  type: ActivityType;
  data: ActivityData;
};

const activityIconMap: Record<ActivityType, ElementType> = {
  [ActivityType.TRANSFER_RECEIVED]: CashIcon,
  [ActivityType.DOCUMENT_SIGNED]: DocumentTextIcon,
  [ActivityType.DOCUMENT_RESET]: RefreshIcon,
  [ActivityType.SUBSCRIPTION_COMPLETED]: CheckCircleIcon,
  [ActivityType.SUBSCRIPTION_VALIDATED]: ShieldCheckIcon,
  [ActivityType.INVEST_AMOUNT_REVIEWED]: PencilAltIcon,
};

/**
 * Activity.
 * Displays the details of an activity within a step of a Stepper.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function Activity({ data, type, date }: Props): JSX.Element {
  const ActivityIcon = activityIconMap[type];

  const RenderActivityComponent = (): JSX.Element | null => {
    switch (type) {
      case ActivityType.TRANSFER_RECEIVED: {
        const { debtorName, label, reference, amount } = data as TransferReceivedData;
        return (
          <TransferReceived
            amount={amount}
            date={date}
            debtorName={debtorName}
            label={label}
            reference={reference}
          />
        );
      }
      case ActivityType.DOCUMENT_SIGNED: {
        const { documentType } = data as DocumentSignedData;
        return <DocumentSigned date={date} documentType={documentType} />;
      }
      case ActivityType.SUBSCRIPTION_COMPLETED: {
        return <SubscriptionCompleted date={date} />;
      }
      case ActivityType.SUBSCRIPTION_VALIDATED: {
        const { validatorName } = data as SubscriptionValidatedData;

        return <SubscriptionValidated date={date} validatorName={validatorName ?? undefined} />;
      }
      case ActivityType.INVEST_AMOUNT_REVIEWED: {
        const { previousAmount, newAmount } = data as InvestAmountReviewedData;

        return (
          <InvestAmountReviewed date={date} newAmount={newAmount} previousAmount={previousAmount} />
        );
      }
      case ActivityType.DOCUMENT_RESET: {
        const { documentType } = data as DocumentResetData;
        return <DocumentReset date={date} documentType={documentType} />;
      }
      default:
        return null;
    }
  };
  return (
    <Step style={{ width: '100%' }}>
      <StepIndicator>
        <StepStatus incomplete={<Icon as={ActivityIcon} boxSize="16px" color="gray.400" />} />
      </StepIndicator>
      <Box color="gray.700" flex="1" fontSize="xs" mb="8">
        <RenderActivityComponent />
      </Box>
      <StepSeparator />
    </Step>
  );
}

export type ActivityProps = Props;
